import React from 'react';
import Tooltip from '@material-ui/core/Tooltip';

import { AZURE_PERMISSIONS } from '@silverfort-nodelibs/common/azure/aad-consts';
import { NEW_AZURE_CONNECTOR_FIELDS } from '../../common/fields';
import TextInput from '../Controls/TextInput/TextInput';
import Label from '../Controls/Label/Label';
import Button from '../Controls/Button/Button';

import styles from './NewAzureConnector.module.css';
import integrationNotActive from './Images/integration_not_connected.png';
import integrationActive from './Images/integration_connected.png';
import integrationError from './Images/integration_error.png';
import { SUBCATEGORIES } from '../../common/categories';
import { SettingsFeatureLock } from '../LockedFeatures/SettingsFeatureLock';

const SIGN_IN_PERMISSIONS = [AZURE_PERMISSIONS.AUDIT_LOGS, AZURE_PERMISSIONS.DIRECTORY];

const AzureConnector = ({
    azureConnector,
    syncText,
    syncError,
    showSyncText,
    changedFunc,
    onSync,
    isDuringSync,
    disabled
}) => {
    const tenantId = azureConnector[NEW_AZURE_CONNECTOR_FIELDS.TENANT_ID.ID];
    const tenantName = azureConnector[NEW_AZURE_CONNECTOR_FIELDS.TENANT_NAME.ID];
    const clientId = azureConnector[NEW_AZURE_CONNECTOR_FIELDS.CLIENT_ID.ID];
    const clientSecret = azureConnector[NEW_AZURE_CONNECTOR_FIELDS.CLIENT_SECRET.ID];
    const hasClientSecret = azureConnector[NEW_AZURE_CONNECTOR_FIELDS.HAS_CLIENT_SECRET.ID];
    const roles = azureConnector[NEW_AZURE_CONNECTOR_FIELDS.ROLES.ID];

    const renderTooltip = (permissions, activeRolesSet) => {
        return (
            <div className={styles.tooltipContainer}>
                <div className={styles.boldText}>Required Permissions:</div>
                {permissions.map((permission) => (
                    <div key={permission} className={styles.tooltipPermissions}>
                        <div>{permission}</div>
                        <img src={activeRolesSet.has(permission) ? integrationActive : integrationError} alt="" />
                    </div>
                ))}
            </div>
        );
    };

    const renderIntegrationRow = (name, permissions, activeRolesSet) => {
        const isActive = permissions.every((permission) => activeRolesSet.has(permission));
        return (
            <div className={styles.azureIntegrationsRow}>
                <div>{name}</div>
                <Tooltip
                    title={renderTooltip(permissions, activeRolesSet)}
                    arrow={true}
                    placement="top"
                    classes={{ tooltip: 'generic-tooltip' }}>
                    <div>
                        <img src={isActive ? integrationActive : integrationNotActive} alt="" />
                    </div>
                </Tooltip>
            </div>
        );
    };

    const renderSyncText = () => {
        if (!showSyncText) {
            return null;
        }

        let text = '';
        let textClass = styles.successMsg;
        if (syncError) {
            text = 'Sync failed. Check your input and try again.';
            textClass = styles.errorMsg;
        } else {
            const isSignInActive = SIGN_IN_PERMISSIONS.every((permission) => activeRolesSet.has(permission));
            if (!isSignInActive) {
                text = 'Sync ended successfully. Permissions are missing to complete the integrations.';
                textClass = styles.warningMsg;
            } else {
                text = 'Sign-in logs configured successfully.';
            }
        }
        return <div className={textClass}>{text}</div>;
    };

    const activeRolesSet = new Set(roles.value);

    return (
        <div className={`${styles.azureConnector}${disabled ? ' ' + styles.disabled : ''}`}>
            <TextInput
                field={tenantId}
                color="grey"
                changed={changedFunc}
                disabled={disabled}
                featureLockId={SUBCATEGORIES.NEW_AZURE_CONNECTORS.ID}
                featureLockIsTabId={true}
            />
            <TextInput
                field={tenantName}
                color="grey"
                changed={changedFunc}
                disabled={disabled}
                featureLockId={SUBCATEGORIES.NEW_AZURE_CONNECTORS.ID}
                featureLockIsTabId={true}
            />
            <TextInput
                field={clientId}
                color="grey"
                changed={changedFunc}
                disabled={disabled}
                featureLockId={SUBCATEGORIES.NEW_AZURE_CONNECTORS.ID}
                featureLockIsTabId={true}
            />
            <TextInput
                field={clientSecret}
                placeholder={hasClientSecret.value ? '******' : ''}
                type="password"
                color="grey"
                changed={changedFunc}
                disabled={disabled}
                featureLockId={SUBCATEGORIES.NEW_AZURE_CONNECTORS.ID}
                featureLockIsTabId={true}
            />
            <div className={styles.azureIntegrationsContainer}>
                <Label value="Entra ID Integrations" />
                <div className="flex justify-between mb-0.5">
                    <div className="flex gap-12 mt-0.5">
                        {renderIntegrationRow('Sign-in Logs', SIGN_IN_PERMISSIONS, activeRolesSet)}
                    </div>
                    <SettingsFeatureLock id={SUBCATEGORIES.NEW_AZURE_CONNECTORS.ID} isTabId={true}>
                        <Button disabled={isDuringSync || disabled} onClick={onSync}>
                            Sync
                        </Button>
                    </SettingsFeatureLock>
                </div>
            </div>
            {renderSyncText()}
        </div>
    );
};

export default React.memo(AzureConnector);
